import {
  addMonths,
  differenceInDays,
  endOfDay,
  endOfMonth,
  getDaysInMonth,
  isAfter,
  isBefore,
  isThisMonth,
  startOfDay,
  startOfMonth
} from 'date-fns';
import {
  IMediaItemDuration,
  IViewMode,
  IViewModeColumn,
  MediaItem,
  TimelineDataItem,
  TreeItem,
  ViewModeType
} from '../store/types';
import { getDiffRange, getOverlappingRange, monthNames } from './utils';

export class MonthsViewMode implements IViewMode {
  public type = ViewModeType.Months;
  public columns: IViewModeColumn[];
  constructor() {
    this.computeColumns = this.computeColumns.bind(this);
  }

  public computeColumns = (startDate: Date, endDate: Date) => {
    this.columns = [];
    startDate = startOfMonth(startDate);
    endDate = endOfMonth(endDate);
    let currDate = startDate;

    const width = 100;
    while (!isAfter(currDate, endDate)) {
      const s = startOfMonth(currDate);
      const e = endOfMonth(currDate);
      this.columns.push({
        startDate: s,
        endDate: e,
        subtitle: monthNames[currDate.getMonth()] + ' ' + currDate.getFullYear(),
        title: (currDate.getMonth() + 1).toString(),
        width,
        headerCellClassModifier: '',
        headerTitleClassModifier: 'months',
        headerSubtitleClassModifier: 'months',
        ...this.isColumnNow(currDate, width)
      });
      currDate = addMonths(currDate, 1);
    }
  };

  public applyToMedia = (media: MediaItem, treeItem: TreeItem): TimelineDataItem[] =>
    this.columns.map<TimelineDataItem>((c, i) => {
      const mediaColumnIntersection = getOverlappingRange(c.startDate, c.endDate, media.startDate, media.endDate);

      // start by verifying some simple situations:

      // when media has no overlap with the column whatsoever - return no durations
      if (!mediaColumnIntersection.overlap) {
        return {
          colIndex: i,
          mediaType: media.mediaType,
          durations: []
        };
      }

      const numberOfDaysInMonth = getDaysInMonth(c.startDate);

      // when the column is totally inside of the media and its parents ranges - then return the overlap between the col and the media and set it as inside the valid range
      if (
        treeItem.startDate &&
        treeItem.endDate &&
        isBefore(treeItem.startDate, c.startDate) &&
        isBefore(media.startDate, c.startDate) &&
        isAfter(media.startDate, c.startDate) &&
        isAfter(treeItem.startDate, c.startDate)
      ) {
        return {
          colIndex: i,
          mediaType: media.mediaType,
          durations: [
            {
              start: 0,
              end: 1,
              insideValidRange: true
            }
          ]
        };
      }

      // when the media is completely outside of the parents ranges - then just return the overlap between the col and the media and set it as outside the valid range
      if (!(treeItem.startDate && treeItem.endDate)) {
        return {
          colIndex: i,
          mediaType: media.mediaType,
          durations: [
            {
              ...this.getDurationBarCoordinatesForRangeAndColumn(
                c,
                media.startDate,
                media.endDate,
                numberOfDaysInMonth
              ),
              insideValidRange: false
            }
          ]
        };
      }

      // now with some more tricky cases in which we have both media range and parents range somewhat overlapped with the column range and we need to determine how exactly

      // start by reducing the parent range to the column
      const treeItemColumnIntersection = getOverlappingRange(
        c.startDate,
        c.endDate,
        treeItem.startDate,
        treeItem.endDate
      );

      // we now have the media and parents intersection with the column range.
      const durations: IMediaItemDuration[] = [];

      // we perform a diff on these two. keep in mind that the parent range(the one on the TreeItem) is inside the media range due to how it is camputed!!!
      // this means that the diffs(parts that do not overlap) represent the media outside the parent range
      const diff = getDiffRange(
        mediaColumnIntersection.start,
        media.endDate,
        treeItemColumnIntersection.start,
        treeItemColumnIntersection.end
      );

      // first the diff on the left of the common range
      if (diff.left) {
        durations.push({
          ...this.getDurationBarCoordinatesForRangeAndColumn(c, diff.left.start, diff.left.end, numberOfDaysInMonth),
          insideValidRange: false
        });
      }

      // now the common range
      if (diff.common) {
        durations.push({
          ...this.getDurationBarCoordinatesForRangeAndColumn(
            c,
            diff.common.start,
            diff.common.end,
            numberOfDaysInMonth
          ),
          insideValidRange: true
        });
      }

      // now the diff on the right of the common range
      if (diff.right) {
        durations.push({
          ...this.getDurationBarCoordinatesForRangeAndColumn(c, diff.right.start, diff.right.end, numberOfDaysInMonth),
          insideValidRange: false
        });
      }
      return {
        colIndex: i,
        mediaType: media.mediaType,
        durations
      };
    });

  private isColumnNow = (date: Date, width: number) => {
    const includesNow = isThisMonth(date);
    if (!includesNow) {
      return { includesNow, nowPosition: -1 };
    }
    const now = new Date();
    const beginningOfMonth = startOfMonth(now);
    const numberOfDaysInMonth = getDaysInMonth(beginningOfMonth);
    const nowPosition = (differenceInDays(now, beginningOfMonth) / numberOfDaysInMonth) * width;
    return { includesNow, nowPosition };
  };

  private getDurationBarCoordinatesForRangeAndColumn(
    col: IViewModeColumn,
    startDate: Date,
    endDate: Date,
    totalDays: number
  ) {
    const startDayDiff = differenceInDays(startDate, col.startDate);
    const start = startDayDiff / totalDays;
    const endDayDiff = isAfter(endOfDay(endDate), startOfDay(col.endDate))
      ? totalDays
      : differenceInDays(endDate, col.startDate);
    const end = endDayDiff / totalDays;
    return { start, end };
  }
}



// WEBPACK FOOTER //
// ./src/viewModes/MonthsViewMode.ts